import {
	compareNonProductionChronological,
	NonProductionStackItem,
} from "@/shared/utils/nonProductionEvents";
import { getMomentTz, TimeService } from "@dexteel/mesf-core";
import { EventSegment } from "../models/Event";

const moment = getMomentTz();

export const startOfMonth = (date: Date) =>
	new Date(date.getFullYear(), date.getMonth(), 1);

export const endOfMonth = (date: Date) =>
	new Date(date.getFullYear(), date.getMonth() + 1, 0);

export const startOfWeek = (date: Date, startsOn: number = 1) => {
	const day = date.getDay();
	const diff = (day < startsOn ? 7 : 0) + day - startsOn;
	const result = new Date(date);
	result.setDate(date.getDate() - diff);
	return result;
};

export const addDays = (date: Date, days: number) => {
	const result = new Date(date);
	result.setDate(date.getDate() + days);
	return result;
};

export const countSectionsByStatus = (
	segments: EventSegment[],
	statusCode: string,
) => {
	const sectionSet = new Set<number>();

	for (const segment of segments) {
		if (!segment || !segment.event) continue;

		if (segment.event.BilletsStatusCode === statusCode) {
			const sid = segment.event.SectionId;
			if (sid !== null && sid !== undefined) {
				sectionSet.add(sid);
			}
		}
	}

	return sectionSet.size;
};

export const isSameDay = (date1: Date, date2: Date) => {
	return (
		date1.getFullYear() === date2.getFullYear() &&
		date1.getMonth() === date2.getMonth() &&
		date1.getDate() === date2.getDate()
	);
};

export const isToday = (date: Date) => isSameDay(date, new Date());

export const getDayKey = (date: Date) => {
	return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

export const isDayInCurrentMonth = (date: Date, currentDate: Date): boolean => {
	return (
		date.getMonth() === currentDate.getMonth() &&
		date.getFullYear() === currentDate.getFullYear()
	);
};

export const isDateInDragRange = (
	date: Date,
	dragStartDate: Date | null,
	dragEndDate: Date | null,
): boolean => {
	if (!dragStartDate || !dragEndDate) return false;
	const start = dragStartDate.getTime();
	const end = dragEndDate.getTime();
	const dateTime = date.getTime();
	return dateTime >= start && dateTime <= end;
};

const toStackItem = (segment: EventSegment): NonProductionStackItem => ({
	start: segment.originalStart.getTime(),
	end: segment.event.End
		? new Date(segment.event.End).getTime()
		: Number.MAX_SAFE_INTEGER,
	eventTypeCode: segment.event.EventTypeCode,
	createdAt: segment.event.CreatedAt
		? new Date(segment.event.CreatedAt).getTime()
		: null,
	eventId: segment.event.EventId,
});

export const compareEventSegments = (
	a: EventSegment,
	b: EventSegment,
): number => compareNonProductionChronological(toStackItem(a), toStackItem(b));

export const organizeEventsInRows = (
	segments: EventSegment[],
): EventSegment[][] => {
	const rows: EventSegment[][] = [];
	// Events must overlap by more than this to be in different rows
	const OVERLAP_BUFFER_MS = 30_000;

	const sortedSegments = [...segments].sort(compareEventSegments);

	sortedSegments.forEach((segment) => {
		let placedInRow = false;
		for (let i = 0; i < rows.length; i++) {
			const row = rows[i];
			let canFit = true;

			for (const existingSegment of row) {
				// Check if events truly overlap (more than buffer)
				const newEventStart = segment.originalStart.getTime();
				const newEventEnd = segment.originalEnd.getTime();
				const existingEventStart = existingSegment.originalStart.getTime();
				const existingEventEnd = existingSegment.originalEnd.getTime();

				if (
					newEventStart < existingEventEnd - OVERLAP_BUFFER_MS &&
					newEventEnd > existingEventStart + OVERLAP_BUFFER_MS
				) {
					canFit = false;
					break;
				}
			}

			if (canFit) {
				row.push(segment);
				placedInRow = true;
				break;
			}
		}

		if (!placedInRow) {
			rows.push([segment]);
		}
	});

	return rows;
};

export const calculateNewEventDates = (
	originalStart: Date,
	originalEnd: Date,
	targetDate: Date | null,
): { start: Date; end: Date } | null => {
	if (!targetDate) return null;

	const serverTz = TimeService.getInstance().getServerTimeZone();
	const originalStartMoment = moment.tz(originalStart, serverTz);
	const originalEndMoment = moment.tz(originalEnd, serverTz);

	// Calculate the duration of the event
	const durationMs =
		originalEndMoment.valueOf() - originalStartMoment.valueOf();

	// Create new start at target date with original start time
	const targetMoment = moment.tz(targetDate, serverTz).startOf("day");
	const newStartMoment = targetMoment
		.clone()
		.hour(originalStartMoment.hour())
		.minute(originalStartMoment.minute())
		.second(originalStartMoment.second())
		.millisecond(originalStartMoment.millisecond());

	// Apply the same duration to get the new end
	const newEndMoment = newStartMoment.clone().add(durationMs, "milliseconds");

	return {
		start: newStartMoment.toDate(),
		end: newEndMoment.toDate(),
	};
};
